import { AVAILABLE_COUNTRIES } from "@/constants/campaignBuilder";
import type { PhoneCountry, RecipientFormDraft } from "@/interfaces/redemption";
import { dialCode } from "@/utils/phone-helper";

/** A type-filter tab in the recipient collection bar. */
export interface TypeTab {
  label: string;
  value: string;
}

/**
 * The recipient feed's top-level type tabs. `value` maps to the coarse type
 * bucket `giftTypeFromCategory` derives from a product's category
 * ("all" shows everything). Ported from the prototype's TYPE_TABS.
 */
export const GIFT_TYPE_TABS: TypeTab[] = [
  { label: "All Gifts", value: "all" },
  { label: "Tangible Gifts", value: "physical" },
  { label: "Charity Gifts", value: "charity" },
  { label: "Experiences", value: "experiences" },
];

/** Gifts shown per "page" in the feed; "View more" reveals another page. */
export const GIFT_FEED_PAGE_SIZE = 12;

/* ── Recipient-details form (RFW-7) ── */

/** Email pattern for the recipient form (loose; the server DTO re-validates). */
export const RECIPIENT_EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Pristine recipient-details form draft. */
export const EMPTY_RECIPIENT_FORM: RecipientFormDraft = {
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  phoneDial: "+1",
  phoneCountry: "",
};

/** Phone picker's default / fallback when the list somehow lacks US. */
export const PHONE_FALLBACK: PhoneCountry = {
  iso: "US",
  name: "United States",
  dial: "+1",
  flag: "🇺🇸",
};

/**
 * Phone-picker countries: the owned shipping allowlist (name/flag) joined with
 * libphonenumber's calling codes. Countries libphonenumber doesn't recognise are dropped.
 */
export const PHONE_COUNTRIES: PhoneCountry[] = AVAILABLE_COUNTRIES.map((c) => ({
  iso: c.code,
  name: c.name,
  flag: c.flag,
  dial: dialCode(c.code),
})).filter((c) => c.dial);

/** Line-art "winged gift box" flourish that plays once behind the thank-you note (RFW-9). */
export const MESSAGE_FLOURISH_VIDEO =
  "https://cdn.shopify.com/videos/c/o/v/b52a2c47ce3741dd8dccd26731e4978c.mp4";

/** Gift envelope graphic — the "note sent" acknowledgement mark (RFW-9). */
export const GIFT_ENVELOPE_ICON =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/black-envelope-full.png?v=1783846158";

/* ── Closing chapter: acknowledgement + impact reveal (RFW-9 / RFW-10) ── */

/**
 * How long the "note sent" acknowledgement holds before the closing impact reveal
 * takes over. Only reached when a real note was sent — skip / empty send go straight through.
 */
export const ACK_HOLD_MS = 2000;

export const GET_STARTED_URL = "/sign-up";

/* ── Fold-and-fly envelope hand-off (RFW-8) — swappable brand seal + sound cues ── */

/** Branded "g" wax-seal stamped over the envelope join as it closes. */
export const ENVELOPE_SEAL_IMAGE =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/2_4f3d02aa-2bb2-4bb8-81dc-f913245afe9f.png?v=1783487027";

/** Paper-fold sound cue — spans the top + bottom folds. */
export const ENVELOPE_FOLD_SFX =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/GAMEBoard-Card_folding_being_p-Elevenlabs.mp3?v=1783574798";

/** Seal-press sound cue — a gentle rub as the seal stamps. */
export const ENVELOPE_SEAL_SFX =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/a_gentle_quick_rub_o__4-1783575620041.mp3?v=1783575639";

/** Swoosh sound cue as the sealed envelope flies off. */
export const ENVELOPE_SWOOSH_SFX =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/WHSH-short_swosh-Elevenlabs.mp3?v=1783559810";

/* ── Well Told custom-map products ── */

/** Well Told's partner hometown-autocomplete widget (v1 loaded the same script). */
export const WELL_TOLD_SCRIPT_URL =
  "https://welltold.s3.amazonaws.com/scripts/partner-hometown-autocomplete/v1/index.umd.js";

/** US regions Well Told has no state map for (v1 `hideForWellTold`). */
export const WELL_TOLD_HIDDEN_STATE_ABBRS = ["DC"];
